import { DateRange } from 'react-day-picker';
import type { FilterControl, MultiSelectControl, SingleSelectControl } from '../../../../../types/filterControl';
import type { FilterSelectOption } from '../../../../../types/table';

export type SelectFilterControl = MultiSelectControl | SingleSelectControl;

export const hasDateRangeValue = (range: DateRange | undefined) => !!(range?.from || range?.to);

const filterOptionsLimit = 8;

/** Which options show: the first few until expanded, and the search input once there are many or a search is set. */
export const getOptionsDisplay = ({
  control,
  options,
  searchValue,
  isExpanded
}: {
  control: SelectFilterControl;
  /** The options left after search. */
  options: FilterSelectOption[];
  searchValue: string;
  isExpanded: boolean;
}) => {
  const visibleOptionsLimit = control.defaultVisibleOptions ?? filterOptionsLimit;
  const hasManyOptions = control.options.length > visibleOptionsLimit;
  const shouldShowSearch = hasManyOptions || searchValue.length > 0;
  const displayOptions = hasManyOptions && !isExpanded ? options.slice(0, visibleOptionsLimit) : options;
  const remainingCount = options.length - visibleOptionsLimit;

  return { displayOptions, hasManyOptions, shouldShowSearch, remainingCount };
};

export type SelectionAction = 'include' | 'exclude' | 'clear';

const without = <T>(values: T[], value: T) => values.filter((v) => v !== value);
const withValue = <T>(values: T[], value: T) => (values.includes(value) ? values : [...values, value]);

/**
 * Include, Exclude or Clear one option as a single `onChange`: a value is never both included and excluded, and
 * a second write would start from the URL before the first landed and drop it.
 */
export const changeSelection = (control: SelectFilterControl, value: string | number, action: SelectionAction) => {
  const exclude =
    action === 'exclude' ? withValue(control.value.exclude, value) : without(control.value.exclude, value);
  if (control.type === 'single-select') {
    const current = control.value.include;
    const keptInclude = current === value ? null : current;
    control.onChange({ include: action === 'include' ? value : keptInclude, exclude });
    return;
  }
  const include = control.value.include;
  control.onChange({ include: action === 'include' ? withValue(include, value) : without(include, value), exclude });
};

/**
 * A described option's label holds its tooltip trigger, a button with a name of its own, and Chrome
 * names the checkbox after that button instead of the label text. Such an option is named explicitly.
 */
export function describedOptionName(option: FilterSelectOption) {
  if (!option.description) {
    return undefined;
  }
  return option.count === undefined ? option.label : `${option.label} (${option.count})`;
}

/** Below this many filters the list is short enough to scan, so the title search stays out of the way. */
const filterSearchThreshold = 6;

/**
 * The sidebar's own filter list narrowed by title. An active filter always stays listed: hiding it would leave
 * the list narrowed by something the user can no longer see or clear.
 */
export const getMatchingControls = (controls: FilterControl[], search: string) => {
  const term = search.trim().toLowerCase();
  const shouldShowSearch = controls.length >= filterSearchThreshold || term.length > 0;
  if (!term) {
    return { visibleControls: controls, shouldShowSearch };
  }
  return {
    visibleControls: controls.filter((control) => control.title.toLowerCase().includes(term) || !control.isInitial),
    shouldShowSearch
  };
};
